import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import HeritageMapFilter from '../../components/ui/HeritageMapFilter';
import MapContainer from './components/MapContainer';
import SitePopup from './components/SitePopup';
import MapStats from './components/MapStats';
import QuickActions from './components/QuickActions';

const HeritageInteractiveMap = () => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    cultural: true,
    natural: true,
    showAll: true,
    trails: true,
    montenegroTrails: true,
    serbiaTrails: true
  });

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleMarkerClick = (site) => {
    setSelectedSite(site);
  };

  const handleClosePopup = () => {
    setSelectedSite(null);
  };

  const handleViewDetails = (site) => {
    // Navigate to trail details or tourism offerings based on site type
    if (site?.type === 'cultural') {
      navigate('/trail-details', { state: { siteId: site?.id } });
    } else {
      navigate('/tourism-offerings', { state: { siteId: site?.id } });
    }
  };

  const toggleMobileFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  return (
    <>
      <Helmet>
        <title>Heritage Interactive Map - Creative Twinning</title>
        <meta 
          name="description" 
          content="Explore cultural and natural heritage sites across Serbia and Montenegro through our interactive map. Discover thematic trails connecting historical and natural landmarks." 
        />
        <meta name="keywords" content="heritage map, Serbia Montenegro, cultural sites, natural parks, interactive map, thematic trails, tourism" />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        {/* Main Content with proper z-index hierarchy */}
        <main className="pt-16 relative flex-1 z-0">
          {/* Desktop Layout */}
          <div className="hidden lg:flex h-[calc(100vh-4rem)]">
            {/* Sidebar with navigation level z-index */}
            <div className="w-80 bg-card border-r border-border shadow-lg overflow-y-auto relative z-navigation">
              <div className="p-6 space-y-6">
                {/* Page Header */}
                <div>
                  <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                    Heritage Interactive Map
                  </h1>
                  <p className="text-muted-foreground leading-relaxed">
                    Explore cultural and natural heritage sites across Serbia and Montenegro. 
                    Click on map markers to discover detailed information about each location, 
                    or follow thematic trail routes connecting related sites.
                  </p>
                </div>

                {/* Map Statistics */}
                <MapStats activeFilters={activeFilters} />

                {/* Filter Controls */}
                <HeritageMapFilter
                  activeFilters={activeFilters}
                  onFilterChange={handleFilterChange}
                />

                {/* Quick Actions */}
                <QuickActions />
              </div>
            </div>

            {/* Map Area with base z-index */}
            <div className="flex-1 relative z-10">
              <MapContainer
                activeFilters={activeFilters}
                onMarkerClick={handleMarkerClick}
                selectedMarker={selectedSite}
              />
            </div>
          </div>

          {/* Mobile Layout with proper z-index */}
          <div className="lg:hidden">
            {/* Mobile Header */}
            <div className="bg-card border-b border-border p-4 relative z-navigation">
              <h1 className="font-heading font-bold text-xl text-foreground mb-1">
                Heritage Map
              </h1>
              <p className="text-sm text-muted-foreground">
                Explore heritage sites across the region
              </p>
            </div>

            {/* Mobile Map */}
            <div className="h-[calc(100vh-12rem)] relative z-10">
              <MapContainer
                activeFilters={activeFilters}
                onMarkerClick={handleMarkerClick}
                selectedMarker={selectedSite}
              />
            </div>

            {/* Mobile Stats Bar */}
            <div className="bg-card border-t border-border p-4 relative z-navigation">
              <MapStats activeFilters={activeFilters} />
            </div>
          </div>

          {/* Mobile Filter Overlay with mobile menu z-index */}
          <HeritageMapFilter
            isOpen={isFilterOpen}
            onToggle={toggleMobileFilter}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            className="z-mobile-menu"
          />

          {/* Site Details Popup with highest z-index for popups */}
          {selectedSite && (
            <SitePopup
              site={selectedSite}
              onClose={handleClosePopup}
              onViewDetails={handleViewDetails}
            />
          )}
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default HeritageInteractiveMap;